import * as React from "react";

export interface UseVirtualListOptions {
	/** Initial and increment batch size for rendering items. Default: 50 */
	batchSize?: number;
	/** Root margin for IntersectionObserver to trigger pre-fetching next batch. Default: "100px" */
	rootMargin?: string;
	/** The element used as the viewport for checking visibility of the target. Default: null (browser viewport) */
	root?: Element | Document | null;
}

export interface UseVirtualListReturn<T> {
	/** Slice of items currently visible / mounted in DOM */
	visibleItems: T[];
	/** True if there are remaining items not yet mounted */
	hasMore: boolean;
	/** Ref to attach to the sentinel bottom element */
	sentinelRef: React.RefObject<HTMLDivElement | null>;
	/** Reset visible items count back to batchSize */
	reset: () => void;
	/** Current number of visible items */
	visibleCount: number;
}

/**
 * Lightweight slice-based progressive virtualization hook using IntersectionObserver.
 * Zero external dependencies. Ideal for menus, dropdowns, and popup lists.
 */
export function useVirtualList<T>(
	items: T[],
	options: UseVirtualListOptions = {},
): UseVirtualListReturn<T> {
	const { batchSize = 50, rootMargin = "100px", root = null } = options;
	const [visibleCount, setVisibleCount] = React.useState(batchSize);
	const sentinelRef = React.useRef<HTMLDivElement | null>(null);

	// Reset visibleCount when items array changes or is reset
	React.useEffect(() => {
		void items;
		setVisibleCount(batchSize);
	}, [items, batchSize]);

	const hasMore = visibleCount < items.length;

	React.useEffect(() => {
		const target = sentinelRef.current;
		if (!target || !hasMore) return;

		if (typeof IntersectionObserver === "undefined") {
			setVisibleCount(items.length);
			return;
		}

		const observer = new IntersectionObserver(
			(entries) => {
				const [entry] = entries;
				if (entry?.isIntersecting) {
					setVisibleCount((prev) => Math.min(prev + batchSize, items.length));
				}
			},
			{
				root: root ?? null,
				rootMargin,
			},
		);

		observer.observe(target);

		return () => {
			observer.disconnect();
		};
	}, [hasMore, batchSize, items.length, rootMargin, root]);

	const visibleItems = React.useMemo(
		() => items.slice(0, visibleCount),
		[items, visibleCount],
	);

	const reset = React.useCallback(() => {
		setVisibleCount(batchSize);
	}, [batchSize]);

	return {
		visibleItems,
		hasMore,
		sentinelRef,
		reset,
		visibleCount,
	};
}
